import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import bindAll from 'lodash.bindall';

import VM from 'scratch-vm';
import CloudProvider from '../lib/cloud-provider';

import {
    showAlertWithTimeout
} from '../reducers/alerts';
import {openUsernameModal} from '../reducers/modals';
import {setUsernameInvalid, setCloudHost, setCloudConnectionStatus} from '../reducers/tw';
import {getCommunityCloudProjectId} from './nexus-connected-center';
import {getCloudHost} from './nexus-cloud-host';
import {nexusDebugLog} from './nexus-debug-log';
import {
    getIsShowingProject,
    getIsShowingWithId
} from '../reducers/project-state';

/**
 * TW: Our scratch-vm has an alternative fix to the cloud variable and video sensing privacy concerns.
 */
const DISABLE_WITH_VIDEO_SENSING = false;

/*
 * Higher Order Component to manage the connection to the cloud server.
 * @param {React.Component} WrappedComponent component to manage VM events for
 * @returns {React.Component} connected component with vm events bound to redux
 */
const cloudManagerHOC = function (WrappedComponent) {
    class CloudManager extends React.Component {
        constructor (props) {
            super(props);
            this.cloudProvider = null;
            bindAll(this, [
                'handleCloudDataUpdate',
                'handleExtensionAdded'
            ]);

            this.props.vm.on('HAS_CLOUD_DATA_UPDATE', this.handleCloudDataUpdate);
            this.props.vm.on('EXTENSION_ADDED', this.handleExtensionAdded);
        }
        componentDidMount () {
            this.syncCloudHostFromProps(this.props);
            this.tryConnect(this.props, {});
        }
        componentWillReceiveProps (nextProps) {
            this.syncCloudHostFromProps(nextProps);
        }
        syncCloudHostFromProps (props) {
            const host = this.getEffectiveCloudHost(props);
            if (host && host !== props.reduxCloudHost) {
                this.props.onSetReduxCloudHost(host);
            }
        }
        getEffectiveCloudHost (props) {
            return props.cloudHost || props.reduxCloudHost || getCloudHost();
        }
        isProjectReadyForCloud (props) {
            if (props.isShowingWithId) {
                return true;
            }
            const communityId = getCommunityCloudProjectId();
            return Boolean(
                communityId &&
                props.isShowingProject &&
                props.projectId === communityId
            );
        }
        cloudDiagnostics (props) {
            return {
                cloudHost: this.getEffectiveCloudHost(props),
                username: props.username,
                projectId: props.projectId,
                communityCloudId: getCommunityCloudProjectId(),
                hasCloudData: props.vm && props.vm.runtime.hasCloudData(),
                isShowingWithId: props.isShowingWithId,
                isShowingProject: props.isShowingProject,
                canModifyCloudData: props.canModifyCloudData,
                cloudEnabled: !props.cloudVariablesDisabledByUser,
                hasCloudPermission: props.hasCloudPermission
            };
        }
        componentDidUpdate (prevProps) {
            // TODO need to add cloud provider disconnection logic and cloud data clearing logic
            // when loading a new project e.g. via file upload
            // (and eventually move it out of the vm.clear function)

            this.tryConnect(this.props, prevProps);

            if (
                getCommunityCloudProjectId() &&
                this.props.isShowingProject &&
                !prevProps.isShowingProject &&
                !this.isConnected() &&
                !this.shouldConnect(this.props)
            ) {
                nexusDebugLog('NexusBlocks', 'Cloud not ready after community game load (bootstrap will retry)',
                    this.cloudDiagnostics(this.props), 'warn');
            }
        }
        componentWillUnmount () {
            this.props.vm.off('HAS_CLOUD_DATA_UPDATE', this.handleCloudDataUpdate);
            this.props.vm.off('EXTENSION_ADDED', this.handleExtensionAdded);
            this.disconnectFromCloud();
        }
        canUseCloud (props) {
            return !!(
                props.reduxCloudHost &&
                props.username &&
                props.vm &&
                props.projectId &&
                props.projectId !== '0' &&
                props.hasCloudPermission &&
                !props.cloudVariablesDisabledByUser
            );
        }
        shouldConnect (props) {
            return !this.isConnected() && this.canUseCloud(props) &&
                this.isProjectReadyForCloud(props) && props.vm.runtime.hasCloudData() &&
                props.canModifyCloudData;
        }
        shouldDisconnect (props, prevProps) {
            return this.isConnected() &&
                ( // Can no longer use cloud or cloud provider info is now stale
                    !this.canUseCloud(props) ||
                    !props.vm.runtime.hasCloudData() ||
                    (props.projectId !== prevProps.projectId) ||
                    // tw: username changes are handled in "reconnect"
                    // (props.username !== prevProps.username) ||
                    // Editing someone else's project
                    !props.canModifyCloudData
                );
        }
        shouldReconnect (props, prevProps) {
            return this.isConnected() && (
                props.username !== prevProps.username ||
                this.getEffectiveCloudHost(props) !== this.getEffectiveCloudHost(prevProps)
            );
        }
        isConnected () {
            return this.cloudProvider &&
                this.cloudProvider.connection &&
                this.cloudProvider.connection.readyState === WebSocket.OPEN;
        }
        tryConnect (props, prevProps) {
            if (this.shouldReconnect(props, prevProps)) {
                this.disconnectFromCloud();
                if (this.shouldConnect(props)) {
                    this.connectToCloud();
                }
                return;
            }

            if (this.shouldConnect(props) && !this.shouldConnect(prevProps)) {
                this.connectToCloud();
                return;
            }

            if (this.shouldDisconnect(props, prevProps)) {
                this.disconnectFromCloud();
            }
        }
        connectToCloud () {
            const host = this.getEffectiveCloudHost(this.props);
            nexusDebugLog('NexusBlocks', 'Connecting cloud variables', {
                host,
                projectId: this.props.projectId,
                username: this.props.username
            });
            this.cloudProvider = new CloudProvider(
                host,
                this.props.vm,
                this.props.username,
                this.props.projectId,
                status => {
                    this.props.onSetCloudConnectionStatus(status);
                    nexusDebugLog('NexusBlocks', `Cloud status: ${status}`, {
                        projectId: this.props.projectId,
                        host
                    }, status === 'connected' ? 'info' : 'warn');
                }
            );
            this.cloudProvider.onInvalidUsername = () => {
                nexusDebugLog('NexusBlocks', 'Cloud rejected username', {
                    username: this.props.username
                }, 'error');
                this.props.onInvalidUsername();
            };
            this.props.vm.setCloudProvider(this.cloudProvider);
        }
        disconnectFromCloud () {
            if (this.cloudProvider) {
                this.cloudProvider.requestCloseConnection();
                this.cloudProvider = null;
                this.props.vm.setCloudProvider(null);
            }
            this.props.onSetCloudConnectionStatus('disconnected');
        }
        handleCloudDataUpdate (projectHasCloudData) {
            if (this.isConnected() && !projectHasCloudData) {
                this.disconnectFromCloud();
            } else if (projectHasCloudData) {
                nexusDebugLog('NexusBlocks', 'Project has cloud variables', this.cloudDiagnostics(this.props));
                if (this.shouldConnect(this.props)) {
                    this.props.onShowCloudInfo();
                    this.connectToCloud();
                }
            }
        }
        handleExtensionAdded (categoryInfo) {
            // Note that props.vm.extensionManager.isExtensionLoaded('videoSensing') is still false
            // at the point of this callback, so it is difficult to reuse the canModifyCloudData logic.
            if (DISABLE_WITH_VIDEO_SENSING && categoryInfo.id === 'videoSensing' && this.isConnected()) {
                this.disconnectFromCloud();
            }
        }
        render () {
            const {
                /* eslint-disable no-unused-vars */
                canModifyCloudData,
                cloudHost,
                reduxCloudHost,
                cloudVariablesDisabledByUser,
                onSetReduxCloudHost,
                projectId,
                username,
                hasCloudPermission,
                isShowingWithId,
                onShowCloudInfo,
                onInvalidUsername,
                /* eslint-enable no-unused-vars */
                vm,
                ...componentProps
            } = this.props;
            return (
                <WrappedComponent
                    canUseCloud={this.canUseCloud(this.props)}
                    vm={vm}
                    {...componentProps}
                />
            );
        }
    }

    CloudManager.propTypes = {
        canModifyCloudData: PropTypes.bool.isRequired,
        cloudHost: PropTypes.string,
        reduxCloudHost: PropTypes.string,
        cloudVariablesDisabledByUser: PropTypes.bool.isRequired,
        onSetReduxCloudHost: PropTypes.func,
        hasCloudPermission: PropTypes.bool,
        isShowingWithId: PropTypes.bool.isRequired,
        onInvalidUsername: PropTypes.func,
        onShowCloudInfo: PropTypes.func,
        projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        username: PropTypes.string,
        vm: PropTypes.instanceOf(VM).isRequired
    };

    CloudManager.defaultProps = {
        cloudHost: null,
        onShowCloudInfo: () => {},
        username: null,
        hasCloudPermission: true
    };

    const mapStateToProps = (state, ownProps) => {
        const loadingState = state.scratchGui.projectState.loadingState;
        return {
            reduxCloudHost: state.scratchGui.tw.cloudHost,
            cloudVariablesDisabledByUser: !state.scratchGui.tw.cloud,
            isShowingWithId: getIsShowingWithId(loadingState),
            isShowingProject: getIsShowingProject(loadingState),
            projectId: state.scratchGui.projectState.projectId,
            // if you're editing someone else's project, you can't modify cloud data
            canModifyCloudData: (!state.scratchGui.mode.hasEverEnteredEditor || ownProps.canSave) &&
                // possible security concern if the program attempts to encode webcam data over cloud variables
                !(DISABLE_WITH_VIDEO_SENSING && ownProps.vm.extensionManager.isExtensionLoaded('videoSensing'))
        };
    };

    const mapDispatchToProps = dispatch => ({
        onSetReduxCloudHost: cloudHost => dispatch(setCloudHost(cloudHost)),
        onSetCloudConnectionStatus: cloudConnectionStatus => dispatch(setCloudConnectionStatus(cloudConnectionStatus)),
        onShowCloudInfo: () => showAlertWithTimeout(dispatch, 'cloudInfo'),
        onInvalidUsername: () => {
            dispatch(setUsernameInvalid(true));
            dispatch(openUsernameModal());
        }
    });

    // Allow incoming props to override redux-provided props. Used to mock in tests.
    const mergeProps = (stateProps, dispatchProps, ownProps) => Object.assign(
        {}, stateProps, dispatchProps, ownProps
    );

    return connect(
        mapStateToProps,
        mapDispatchToProps,
        mergeProps
    )(CloudManager);
};

export default cloudManagerHOC;
